/**
 * AuthService for managing user authentication and user management
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import cacheService from './cacheService';
import { createErrorLogger, ErrorType } from '../utils/logger';
import LocalStorageService from '../utils/localStorageService';
import { shouldAttachSessionToken } from './authRouteGuards';

const logger = createErrorLogger('AuthService');

// Session token key (must match AuthContext)
const SESSION_TOKEN_KEY = 'autobot_session_token';

// Add axios interceptor to include Authorization header
axios.interceptors.request.use(
  (config) => {
    // Get session token from localStorage
    const sessionToken = LocalStorageService.getItem(SESSION_TOKEN_KEY);

    if (sessionToken && shouldAttachSessionToken(config.url)) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
      logger.info('axios-interceptor', `Added Authorization header to ${config.url}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class AuthService {
  /**
   * List all users
   * @returns {Promise<array>} List of users
   */
  async listUsers() {
    logger.info('listUsers', 'Fetching all users');

    try {
      const response = await axios.get(`${API_BASE_URL}/auth/users`);
      logger.info('listUsers', `Retrieved ${response.data.users?.length || 0} users`);

      // Map backend snake_case to frontend camelCase
      const users = (response.data.users || []).map(user => ({
        ...user,
        managerId: user.manager_id, // Map snake_case to camelCase
        createdAt: user.created_at,
        updatedAt: user.updated_at
      }));

      return users;
    } catch (error) {
      logger.error('listUsers', 'Error listing users', error, ErrorType.AUTH);
      return [];
    }
  }

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @returns {Promise<object>} Update result
   */
  async updateUserRole(userId, role) {
    logger.info('updateUserRole', `Updating role for user ${userId} to ${role}`);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/update-role`, {
        user_id: userId,
        new_role: role
      });
      logger.info('updateUserRole', 'Role updated successfully', { userId, role });

      // Clear user cache
      cacheService.clear('currentUser');

      return response.data;
    } catch (error) {
      logger.error('updateUserRole', 'Error updating user role', error, ErrorType.AUTH, { userId, role });
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Assign a manager to a user
   * @param {string} userId - User ID
   * @param {string} managerId - Manager ID
   * @returns {Promise<object>} Assignment result
   */
  async assignManager(userId, managerId) {
    logger.info('assignManager', `Assigning manager ${managerId} to user ${userId}`);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/assign-manager`, {
        user_id: userId,
        manager_id: managerId
      });
      logger.info('assignManager', 'Manager assigned successfully', { userId, managerId });

      return response.data;
    } catch (error) {
      logger.error('assignManager', 'Error assigning manager', error, ErrorType.AUTH, { userId, managerId });
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Get current user from cache
   * @returns {object|null} Current user data
   */
  getCurrentUser() {
    const user = cacheService.get('currentUser');
    logger.info('getCurrentUser', user ? 'User found in cache' : 'No user in cache');
    return user;
  }

  /**
   * Set current user in cache
   * @param {object} user - User data to cache
   */
  setCurrentUser(user) {
    logger.info('setCurrentUser', 'Setting user in cache', { userId: user?.id });
    cacheService.set('currentUser', user, 3600000); // Cache for 1 hour instead of 5 seconds
  }

  /**
   * Get auth token from cache
   * @returns {string|null} Auth token
   */
  getAuthToken() {
    const token = cacheService.get('authToken');
    logger.info('getAuthToken', token ? 'Token found in cache' : 'No token in cache');
    return token;
  }

  /**
   * Update user information
   * @param {string} userId - User ID to update
   * @param {string} name - New name (optional)
   * @param {string} email - New email (optional)
   * @param {string} role - New role (optional)
   * @param {string} department - New department (optional)
   * @returns {Promise<object>} Update result
   */
  async updateUser(userId, name, email, role, department = null) {
    logger.info('updateUser', `Updating user ${userId}`, { name, email, role, department });

    try {
      const updateData = { user_id: userId };
      if (name) updateData.name = name;
      if (email) updateData.email = email;
      if (role) updateData.role = role;
      if (department !== null) updateData.department = department;

      const response = await axios.post(`${API_BASE_URL}/auth/update-user`, updateData);
      logger.info('updateUser', 'User updated successfully', { userId });

      // Clear user cache to force refresh
      cacheService.clear('currentUser');

      return response.data;
    } catch (error) {
      logger.error('updateUser', 'Error updating user', error, ErrorType.AUTH, { userId });
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Delete a user
   * @param {string} userId - User ID to delete
   * @returns {Promise<object>} Deletion result
   */
  async deleteUser(userId) {
    logger.info('deleteUser', `Deleting user ${userId}`);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/delete-user`, {
        user_id: userId
      });
      logger.info('deleteUser', 'User deleted successfully', { userId });

      // Clear user cache to force refresh
      cacheService.clear('currentUser');

      return response.data;
    } catch (error) {
      logger.error('deleteUser', 'Error deleting user', error, ErrorType.AUTH, { userId });
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Clear authentication data
   */
  logout() {
    logger.info('logout', 'Clearing authentication data');
    cacheService.clear('currentUser');
    cacheService.clear('authToken');
  }

  /**
   * Validate if user has required permission
   * @param {string} userId - User ID
   * @param {string} permission - Required permission
   * @returns {Promise<boolean>} True if user has permission
   */
  async hasPermission(userId, permission) {
    logger.info('hasPermission', `Checking permission ${permission} for user ${userId}`);

    try {
      // This would typically check against a permissions API
      // For now, returning true as placeholder
      logger.info('hasPermission', 'Permission check not implemented, returning true');
      return true;
    } catch (error) {
      logger.error('hasPermission', 'Error checking permission', error, ErrorType.PERMISSION, { userId, permission });
      return false;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

// Export singleton instance
export default authService;
